import { ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fmt } from '../../../lib/format';
import { KIND_LABEL, dayLabel } from '../../../lib/events';
import { CountItem, InsightEvent, Insights, downloadCsv, dollars, eventLabel, rate } from '../../../lib/eventInsights';
import { inputClass } from '../../../lib/programs';
import { PanelHeader } from '../../ui/Panel';
import Button from '../../ui/Button';
import { tHead, tRow, tTable, tTd, tTh } from './InsightCharts';

type Row = [string, (e: InsightEvent) => ReactNode, ((e: InsightEvent) => string | number | null)?];

const items = (list: CountItem[] | undefined, max = 6) =>
  (list ?? [])
    .slice(0, max)
    .map((i) => `${i.value} ${i.count}`)
    .join(' · ') || '–';

/** A measure counts as empty when every number in it is zero or it has nothing in it ("0 / 0", "", null). */
const isEmpty = (v: string | number | null | undefined) =>
  v === null || v === undefined || (typeof v === 'number' ? v === 0 : /^[\s0./;·–—-]*$/.test(v));
const quietLink = 'text-accent underline underline-offset-[3px] hover:text-ink';

/**
 * Two or three events side by side, chosen with three selects. Measures where every chosen event is zero or empty
 * fold under "Show the other measures" so the differences stand out.
 */
/** Kinds in the order offered; only events of one kind are compared (regional with regional, outreach with outreach). */
const KIND_ORDER = ['national', 'regional', 'pi_meeting', 'outreach'];
const KIND_PLURAL: Record<string, string> = {
  national: 'National meetings',
  regional: 'Regional meetings',
  pi_meeting: 'PI meetings',
  outreach: 'Outreach talks',
};

export default function CompareEvents({ insights, onBack }: { insights: Insights; onBack: () => void }) {
  const all = insights.events;
  const kinds = [
    ...KIND_ORDER.filter((k) => all.some((e) => e.kind === k)),
    ...[...new Set(all.map((e) => e.kind))].filter((k) => !KIND_ORDER.includes(k)),
  ];
  const latestOf = (k: string) =>
    all
      .filter((e) => e.kind === k)
      .slice(-2)
      .map((e) => e.id);
  // Opens on the national meetings when there are two, else the first kind with two events to compare.
  const [kind, setKind] = useState(() => kinds.find((k) => latestOf(k).length > 1) ?? kinds[0] ?? 'national');
  const events = all.filter((e) => e.kind === kind);
  const [ids, setIds] = useState<string[]>(() => latestOf(kind));
  const top = useRef<HTMLDivElement>(null);
  useEffect(() => {
    top.current?.scrollIntoView({ block: 'start' });
  }, []);
  const chooseKind = (k: string) => {
    setKind(k);
    setIds(latestOf(k));
  };
  const [showOther, setShowOther] = useState(false);
  const chosen = ids
    .filter(Boolean)
    .map((id) => events.find((e) => e.id === id))
    .filter(Boolean) as InsightEvent[];
  const admin = insights.moneyVisible;
  const rows: Row[] = [
    [
      'Dates',
      (e) => `${dayLabel(e.days[0])}${e.days.length > 1 ? ` – ${dayLabel(e.days[e.days.length - 1])}` : ''}`,
      (e) => e.days.join(' '),
    ],
    [
      'Place',
      (e) => [e.venue, e.city].filter(Boolean).join(', ') || '–',
      (e) => [e.venue, e.city].filter(Boolean).join(', '),
    ],
    ['On the list', (e) => fmt(e.figures.onList), (e) => e.figures.onList],
    ['Registered', (e) => fmt(e.figures.registered), (e) => e.figures.registered],
    ['Confirmed', (e) => fmt(e.figures.confirmed), (e) => e.figures.confirmed],
    ['Declined', (e) => fmt(e.figures.declined), (e) => e.figures.declined],
    ['Attended', (e) => fmt(e.figures.attended), (e) => e.figures.attended],
    [
      'Per day',
      (e) =>
        `${e.figures.byDay.map((d) => `${dayLabel(d.date)}: ${d.count}`).join(' · ')}${e.figures.dayUnknown ? ` · day not recorded: ${e.figures.dayUnknown}` : ''}`,
      (e) => e.figures.byDay.map((d) => `${d.date} ${d.count}`).join('; '),
    ],
    [
      'In person / online',
      (e) => `${fmt(e.figures.inPerson)} / ${fmt(e.figures.virtual)}`,
      (e) => `${e.figures.inPerson} / ${e.figures.virtual}`,
    ],
    ['No-show rate', (e) => rate(e.figures.noShowRate), (e) => e.figures.noShowRate],
    ['Capacity use', (e) => rate(e.figures.capacityUse), (e) => e.figures.capacityUse],
    [
      'First time / returning',
      (e) => `${fmt(e.firstTime)} / ${fmt(e.returning)}`,
      (e) => `${e.firstTime} / ${e.returning}`,
    ],
    ['By role', (e) => items(e.who.byRole, 8), (e) => items(e.who.byRole, 20)],
    ['Region', (e) => items(e.who.byRegion.items), (e) => items(e.who.byRegion.items, 20)],
    ['Career stage', (e) => items(e.who.byCareerStage.items), (e) => items(e.who.byCareerStage.items, 20)],
    [
      'Registered by 30 days before',
      (e) => rate(e.curve.points.find((p) => p.daysBefore === 30)?.share),
      (e) => e.curve.points.find((p) => p.daysBefore === 30)?.share ?? null,
    ],
    [
      'Hotel: peak rooms / block',
      (e) => `${fmt(e.logistics.hotel.peakRooms)} / ${e.logistics.hotel.block ?? '–'}`,
      (e) => `${e.logistics.hotel.peakRooms} / ${e.logistics.hotel.block ?? ''}`,
    ],
    [
      'Nights per traveller',
      (e) => e.logistics.hotel.nightsPerTraveller ?? '–',
      (e) => e.logistics.hotel.nightsPerTraveller,
    ],
    [
      'Travel',
      (e) => items(e.logistics.travelModes.filter((t) => t.value !== 'Not recorded')),
      (e) => items(e.logistics.travelModes, 20),
    ],
    [
      'Parking / dietary',
      (e) => `${fmt(e.logistics.parking)} / ${fmt(e.logistics.dietary)}`,
      (e) => `${e.logistics.parking} / ${e.logistics.dietary}`,
    ],
    [
      'Sessions / speakers',
      (e) => (e.program ? `${fmt(e.program.sessions)} / ${fmt(e.program.speakers)}` : '–'),
      (e) => (e.program ? `${e.program.sessions} / ${e.program.speakers}` : ''),
    ],
    ['Evaluation responses', (e) => fmt(e.evaluation.responses), (e) => e.evaluation.responses],
    ...(admin
      ? ([
          [
            'Reimbursements',
            (e) => `${fmt(e.money?.reimbursement.count ?? 0)} · ${dollars(e.money?.reimbursement.totalCents)}`,
            (e) => (e.money?.reimbursement.totalCents ?? 0) / 100,
          ],
          [
            'Median days to pay',
            (e) => e.money?.reimbursement.medianDaysToPaid ?? '–',
            (e) => e.money?.reimbursement.medianDaysToPaid ?? null,
          ],
          [
            'Honoraria / gift cards',
            (e) => `${dollars(e.money?.honorarium.totalCents)} / ${dollars(e.money?.gift_card.totalCents)}`,
            (e) => `${(e.money?.honorarium.totalCents ?? 0) / 100} / ${(e.money?.gift_card.totalCents ?? 0) / 100}`,
          ],
          [
            'Vendors and other costs',
            (e) => dollars(e.money?.otherCostActualCents),
            (e) => (e.money?.otherCostActualCents ?? 0) / 100,
          ],
        ] as Row[])
      : []),
  ];
  const pick = (slot: number, id: string) =>
    setIds((cur) => {
      const next = [...cur];
      next[slot] = id;
      return next.slice(0, 3);
    });
  const unique = chosen.filter((e, i) => chosen.findIndex((x) => x.id === e.id) === i);
  const shownRows = rows.filter(([, , raw]) => !raw || unique.some((e) => !isEmpty(raw(e))));
  const folded = rows.filter((r) => !shownRows.includes(r));
  const visible = showOther ? [...shownRows, ...folded] : shownRows;
  const slots: [string, boolean][] = [
    ['First event', false],
    ['Second event', false],
    ['Third event (optional)', true],
  ];
  return (
    <>
      <div ref={top} className="scroll-mt-24" />
      <PanelHeader title={<span className="text-[26px] leading-[34px]">Compare events</span>}>
        <Button onClick={onBack}>Back to trends</Button>
        {admin && unique.length ? (
          <button
            type="button"
            className={`${quietLink} text-[15px] min-h-[44px]`}
            onClick={() =>
              downloadCsv('compare', [
                ['', ...unique.map(eventLabel)],
                ...rows.map(([label, , raw]) => [label, ...unique.map((e) => (raw ? raw(e) : ''))]),
              ])
            }
          >
            CSV
          </button>
        ) : null}
      </PanelHeader>
      {kinds.length > 1 ? (
        <div className="mb-5">
          <div className="text-[15px] leading-5 font-semibold text-ink mb-[6px]" id="compare-kind">
            Compare
          </div>
          <div
            role="group"
            aria-labelledby="compare-kind"
            className="inline-flex flex-wrap rounded-sm border border-line-strong overflow-hidden max-md:flex max-md:w-full"
          >
            {kinds.map((k, i) => (
              <button
                key={k}
                type="button"
                aria-pressed={kind === k}
                onClick={() => chooseKind(k)}
                className={`min-h-[44px] px-3 py-2 text-[15px] leading-5 max-md:flex-1 ${i ? 'border-l border-line-strong' : ''} ${
                  kind === k ? 'bg-ink text-paper font-semibold' : 'text-ink hover:bg-selected'
                }`}
              >
                {KIND_PLURAL[k] ?? KIND_LABEL[k] ?? k}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4 max-w-[1000px] mb-6">
        {slots.map(([label, optional], slot) => (
          <div key={label} className="flex flex-col gap-[6px] min-w-0">
            <label htmlFor={`compare-${slot}`} className="text-[15px] leading-5 font-semibold text-ink">
              {label}
            </label>
            <select
              id={`compare-${slot}`}
              className={inputClass}
              value={ids[slot] ?? ''}
              onChange={(e) =>
                optional && !e.target.value ? setIds((cur) => cur.slice(0, 2)) : pick(slot, e.target.value)
              }
            >
              {optional || !ids[slot] ? <option value="">{optional ? 'None' : 'Choose an event'}</option> : null}
              {events.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.year} · {e.title}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
      {events.length < 2 ? (
        <p className="text-[15px] leading-[22px] text-ink-2">
          Only one {(KIND_PLURAL[kind] ?? kind).toLowerCase().replace(/s$/, '')} so far, so there is nothing to compare
          it with yet.
        </p>
      ) : unique.length < 2 ? (
        <p className="text-[15px] leading-[22px] text-ink-2">Choose two different events to compare.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className={`${tTable} ${unique.length > 2 ? 'min-w-[760px]' : 'min-w-[560px]'}`}>
              <thead>
                <tr className={tHead}>
                  <th className={`${tTh} w-[22%]`}>
                    <span className="sr-only">Measure</span>
                  </th>
                  {unique.map((e) => (
                    <th key={e.id} className={tTh}>
                      <Link to={`/events/${e.id}`}>{e.title}</Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map(([label, show]) => (
                  <tr key={label} className={tRow}>
                    <td className={`${tTd} text-ink-2`}>{label}</td>
                    {unique.map((e) => (
                      <td key={e.id} className={tTd}>
                        {show(e)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {folded.length ? (
            <p className="text-[15px] leading-[22px] text-ink-3 mt-3">
              <button
                type="button"
                aria-expanded={showOther}
                className={quietLink}
                onClick={() => setShowOther(!showOther)}
              >
                {showOther ? 'Hide the other measures' : 'Show the other measures'}
              </button>
              {showOther
                ? null
                : ` · ${folded
                    .map(([l]) => l.toLowerCase())
                    .join(', ')} (zero or empty for ${unique.length === 2 ? 'both' : 'all three'})`}
            </p>
          ) : null}
        </>
      )}
      <div className="mt-8">
        <Button onClick={onBack}>Back to trends</Button>
      </div>
    </>
  );
}
